import { Button, Checkbox, Modal, PinInput, Tooltip } from '@mantine/core'
import { useEffect, useState } from 'react'
import { USPhoneInput } from '@/components/ui/USPhoneInput'
import { IconDeviceMobile, IconInfoCircle, IconMail } from '@tabler/icons-react'
import { showNotification } from '@mantine/notifications'
import { useClient } from '@/hooks/useClient'
import {
  AUTH_CLASSES,
  AUTH_COLORS,
  AUTH_LOGIN_CHECKBOX_STYLES,
  AUTH_LOGIN_PRIMARY_BUTTON_STYLES,
  AUTH_LOGIN_SECONDARY_BUTTON_STYLES,
  AUTH_LOGIN_TEXT_INPUT_STYLES,
} from '../authTheme'

/** The mfa-challenge state returned by /v2/auth/login or /v2/auth/profile-select. */
export type MfaChallenge = {
  challengeId: string
  methods: { phone: boolean; email: boolean }
  maskedPhone?: string
  maskedEmail?: string
}

export type BrokerSuccess = {
  state: 'success'
  accessToken: string
  accessTokenExpiresAt: string
  profileRef: string
  sessionKind: 'provider' | 'patient'
}

type Props = {
  challenge?: MfaChallenge
  onSuccess: (grant: BrokerSuccess) => void
  onCancel: () => void
}

// Matches the gateway's AUTH_V2_OTP_RESEND_COOLDOWN_MS (60 s) — a shorter UI timer lets
// the user click resend while the server still rejects it with "Please wait…".
const timerValue = 60

const phoneRegExp = /^\+?[1-9]\d{7,14}$/

/**
 * The gateway voided the challenge (too many wrong codes, or the account locked) — the
 * dialog is dead; the user must start the sign-in over.
 */
function isChallengeVoided(err: unknown): boolean {
  return !!(err as { data?: { challengeVoided?: boolean } })?.data?.challengeVoided
}

/**
 * Gateway-flow 2FA modal (PD-1512): the same three visual states as the legacy
 * TwoFAModal, driven by the /v2/auth contract — an opaque challengeId instead of a
 * Medplum login id, and the session cookie is set server-side on verify. Phone
 * enrollment (no stored phone → enter one, proven by the OTP) matches the legacy flow.
 */
export const TwoFAModalV2 = ({ challenge, onSuccess, onCancel }: Props) => {
  const client = useClient()

  const [newPhone, setNewPhone] = useState<string | undefined>(undefined)
  const [code, setCode] = useState<string | undefined>(undefined)
  const [isCodeSent, setIsCodeSent] = useState<boolean>(false)
  const [timer, setTimer] = useState<number>(0)
  const [triggerTimer, setTriggerTimer] = useState<boolean>(false)
  const [rememberDevice, setRememberDevice] = useState<boolean>(false)
  const [consent, setConsent] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [isVerifying, setIsVerifying] = useState<boolean>(false)

  const opened = !!challenge
  const hasStoredPhone = !!challenge?.methods.phone

  useEffect(() => {
    // Fresh challenge → fresh modal state.
    setNewPhone(undefined)
    setCode(undefined)
    setIsCodeSent(false)
    setTimer(0)
    setTriggerTimer(false)
    setRememberDevice(false)
    setConsent(false)
    setError(undefined)
    setIsVerifying(false)
  }, [challenge?.challengeId])

  useEffect(() => {
    if (triggerTimer) {
      const interval = setInterval(() => {
        setTimer((currentTimer) => {
          if (currentTimer <= 1) {
            clearInterval(interval)
            setTriggerTimer(false)
            return 0
          }
          return currentTimer - 1
        })
      }, 1000)
      return () => clearInterval(interval)
    }
  }, [triggerTimer])

  const sendCode = async (sendTo: 'email' | 'phone' = 'phone') => {
    if (!challenge) return
    if (!hasStoredPhone && sendTo === 'phone') {
      if (!newPhone || !newPhone.match(phoneRegExp)) {
        showNotification({ color: 'red', message: 'Invalid phone number' })
        return
      }
    }
    setTimer(timerValue)
    try {
      await client.authMfaSend({
        challengeId: challenge.challengeId,
        sendTo,
        ...(!hasStoredPhone && sendTo === 'phone' && { phoneNumber: newPhone }),
      })
      setTriggerTimer(true)
      setIsCodeSent(true)
    } catch (err) {
      setTimer(0)
      showNotification({
        color: 'red',
        message: (err as Error).message || 'Could not send the verification code',
      })
      if (isChallengeVoided(err)) {
        onCancel()
      }
    }
  }

  const verifyCode = async () => {
    if (!challenge || isVerifying) return
    if (!code) {
      showNotification({ color: 'red', message: 'Code has not been provided' })
      return
    }
    // A second Confirm would spend the already-consumed challenge and fail the sign-in, so
    // the button stays locked after success until the dialog closes; only errors unlock it.
    setIsVerifying(true)
    try {
      const grant = await client.authMfaVerify({
        challengeId: challenge.challengeId,
        code,
        rememberDevice,
      })
      onSuccess(grant as BrokerSuccess)
    } catch (err) {
      setIsVerifying(false)
      showNotification({
        color: 'red',
        message: (err as Error).message || 'Invalid code',
      })
      if (isChallengeVoided(err)) {
        // Too many wrong codes / account locked — close the dead dialog and return to
        // the login form instead of letting more codes be typed into a voided challenge.
        onCancel()
      }
    }
  }

  return (
    <Modal
      title={
        <div className="absolute text-[20px]">
          {!hasStoredPhone && !isCodeSent && 'Verify Your Identity'}
          {hasStoredPhone && !isCodeSent && 'Device Verification'}
          {isCodeSent && 'Enter your code'}
        </div>
      }
      opened={opened}
      onClose={onCancel}
      classNames={{
        content: `${AUTH_CLASSES.fontScope} !rounded-xl px-2 pb-2`,
      }}
    >
      {!hasStoredPhone && !isCodeSent && (
        <>
          <p className={`${AUTH_CLASSES.mutedText} mb-4 text-[14px]`}>
            Enter your phone number for a verification code.
          </p>
          <p className={`${AUTH_CLASSES.fieldLabel} mb-[7px]`}>Phone number</p>
          <USPhoneInput
            placeholder="Enter phone number"
            value={newPhone}
            disabled={false}
            onChange={(phone) => {
              setNewPhone(phone)
              if (!phone?.match(phoneRegExp)) {
                setError('Invalid phone number')
              } else {
                setError(undefined)
              }
            }}
            classNames={{ input: AUTH_CLASSES.inputFocus }}
            styles={{
              input: {
                ...AUTH_LOGIN_TEXT_INPUT_STYLES.input,
                ...(error ? { borderColor: AUTH_COLORS.error } : {}),
              },
            }}
          />
          {error && <p className={`${AUTH_CLASSES.errorText} mt-[6px] text-[12px]`}>{error}</p>}

          <div className="mt-4 flex items-start gap-3">
            <Checkbox
              className="pt-1"
              styles={AUTH_LOGIN_CHECKBOX_STYLES}
              defaultChecked={consent}
              onChange={(e) => setConsent(e.currentTarget.checked)}
            />
            <span className={`${AUTH_CLASSES.secondaryText} text-[14px]`}>
              I consent to receive login verification codes via SMS from BeHuman. Message and data
              rates may apply.
            </span>
          </div>
          <div className="mt-4 flex justify-center">
            <Button
              fullWidth
              className={AUTH_CLASSES.primaryButtonState}
              styles={AUTH_LOGIN_PRIMARY_BUTTON_STYLES}
              onClick={() => sendCode()}
              disabled={!consent || timer > 0}
            >
              {timer > 0 ? `Wait ${timer}s` : 'Send code'}
            </Button>
          </div>
        </>
      )}
      {hasStoredPhone && !isCodeSent && (
        <>
          <p className={`${AUTH_CLASSES.mutedText} mb-4 text-[14px]`}>
            For your security, please choose how you'd like to receive your verification code. This
            extra step helps keep your account safe.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Button
              fullWidth
              className={AUTH_CLASSES.primaryButtonState}
              styles={AUTH_LOGIN_PRIMARY_BUTTON_STYLES}
              disabled={timer > 0}
              onClick={() => sendCode()}
            >
              Send verification code to phone{' '}
              {challenge?.maskedPhone ? `(${challenge.maskedPhone})` : ''}{' '}
              {timer > 0 ? `Wait ${timer}s` : ''} <IconDeviceMobile className="ml-2" />
            </Button>
            <div className={`${AUTH_CLASSES.mutedText} text-center text-[12.5px]`}>
              Message and data rates may apply
            </div>
            {challenge?.methods.email && (
              <>
                <div className="relative my-3 flex h-[1px] w-full justify-center bg-[rgba(0,0,0,0.08)]">
                  <div className="absolute top-[-9px] w-[30px] bg-white text-center text-[11.5px] font-medium text-[#ccc]">
                    or
                  </div>
                </div>
                <Button
                  fullWidth
                  className={AUTH_CLASSES.secondaryButtonState}
                  styles={AUTH_LOGIN_SECONDARY_BUTTON_STYLES}
                  disabled={timer > 0}
                  onClick={() => sendCode('email')}
                >
                  Send verification code to email{' '}
                  {challenge?.maskedEmail ? `(${challenge.maskedEmail})` : ''}{' '}
                  {timer > 0 ? `Wait ${timer}s` : ''}
                  <IconMail className="ml-3" />
                </Button>
              </>
            )}
          </div>
        </>
      )}
      {isCodeSent && (
        <>
          <p className={`${AUTH_CLASSES.mutedText} mb-4 text-[14px]`}>Enter your 6-digit code</p>
          <span className={`${AUTH_CLASSES.fieldLabel} mb-[7px] block`}>Verification Code:</span>
          <PinInput
            type={/^[0-9]*$/}
            classNames={{ input: AUTH_CLASSES.inputFocus }}
            styles={{
              root: {
                display: 'flex',
                justifyContent: 'space-between',
                gap: 8,
              },
              input: {
                borderRadius: 12,
                borderWidth: 1.5,
                borderColor: AUTH_COLORS.inputBorder,
                fontSize: 15,
              },
            }}
            autoFocus
            placeholder=""
            length={6}
            inputMode="tel"
            value={code}
            onChange={setCode}
            size="lg"
          />
          <div className="mt-4 flex items-start gap-3">
            <Checkbox
              className="pt-1"
              styles={AUTH_LOGIN_CHECKBOX_STYLES}
              onChange={(e) => setRememberDevice(e.currentTarget.checked)}
            />
            <div className="flex flex-1 items-center justify-between">
              <p className="leading-5">
                <span className={`${AUTH_CLASSES.secondaryText} text-[14px]`}>
                  Trust this device for 30 days
                </span>
                <br />
                <span className={`${AUTH_CLASSES.mutedText} text-[14px]`}>
                  (Not recommended for public devices)
                </span>
              </p>
              <Tooltip
                label={
                  <p className="max-w-[290px] text-[13px] whitespace-pre-wrap">
                    Trusting the device means you will not have to authenticate the device each time
                    you login to system. For your protection you will be asked to verify and trust
                    the device every 30 days.
                  </p>
                }
              >
                <Button variant="transparent" className={`${AUTH_CLASSES.softTextClass} !p-0`}>
                  <IconInfoCircle />
                </Button>
              </Tooltip>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <Button
              className={AUTH_CLASSES.secondaryButtonState}
              styles={AUTH_LOGIN_SECONDARY_BUTTON_STYLES}
              disabled={timer > 0}
              onClick={() => {
                setCode(undefined)
                setIsCodeSent(false)
              }}
            >
              {timer > 0 ? `Wait ${timer}s` : 'Back'}
            </Button>
            <Button
              className={AUTH_CLASSES.primaryButtonState}
              styles={AUTH_LOGIN_PRIMARY_BUTTON_STYLES}
              loading={isVerifying}
              onClick={() => verifyCode()}
            >
              Confirm
            </Button>
          </div>
        </>
      )}
    </Modal>
  )
}
